import clsx from 'clsx';
import { AlertTriangle, SlidersHorizontal } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { outOfScopeSummary, type AttributeScope, type FieldScope, type OutOfScopeValue } from '@pz/core';
import { useM, useQ } from '../lib/hooks';
import { Button, ErrorBox, Loading, Modal, Notice, useToast } from './ui';

/**
 * Values of a Forma field that the project uses (request 20 of round 4): for the whole project, per building (when the
 * analysis takes the buildings from a field) and per location of the Forma tree. Projektzeit never writes to Forma: the
 * selection only decides which values the analysis uses; values outside it count there as "no value" and are listed.
 */

type Target = { kind: 'project' } | { kind: 'building'; key: string } | { kind: 'location'; key: string };
const targetKey = (x: Target) => (x.kind === 'project' ? 'project' : `${x.kind}:${x.key}`);

function listOf(draft: FieldScope, target: Target): string[] | undefined {
  if (target.kind === 'project') return draft.values;
  if (target.kind === 'building') return draft.byBuilding?.[target.key];
  return draft.byLocation?.[target.key];
}

function withList(draft: FieldScope, target: Target, list: string[]): FieldScope {
  const next: FieldScope = { ...draft, byBuilding: { ...draft.byBuilding }, byLocation: { ...draft.byLocation } };
  if (target.kind === 'project') next.values = list;
  else if (target.kind === 'building') {
    if (list.length) next.byBuilding![target.key] = list;
    else delete next.byBuilding![target.key];
  } else if (list.length) next.byLocation![target.key] = list;
  else delete next.byLocation![target.key];
  return next;
}

export function FieldScopeDialog({ projectId, fieldId, open, onClose }: { projectId: string; fieldId: string | null; open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const q = useQ('analysis.scope', { projectId }, { enabled: open && !!fieldId });
  const save = useM('analysis.setScope', { invalidate: ['analysis.', 'projects.'] });
  const field = q.data?.fields.find((f) => f.id === fieldId);
  const [draft, setDraft] = useState<FieldScope>({});
  const [target, setTarget] = useState<Target>({ kind: 'project' });
  useEffect(() => {
    if (open && q.data && fieldId) {
      setDraft(q.data.scope[fieldId] ?? {});
      setTarget({ kind: 'project' });
    }
  }, [open, q.data, fieldId]);
  const all = useMemo(() => field?.values.map((v) => v.value) ?? [], [field]);
  // buildings from a field: one target each (not for the building field itself)
  const buildings = q.data?.building && q.data.building.id !== fieldId ? q.data.building.values : [];
  const locations = q.data?.locations ?? [];
  const own = listOf(draft, target);
  // the project: no list = every value is used; a building or location: no list = the values above it apply
  const checked = new Set(target.kind === 'project' ? (own?.length ? own : all) : (own ?? []));
  const status = (x: Target) => {
    const l = listOf(draft, x);
    if (x.kind === 'project') return l?.length && l.length < all.length ? t('scope.count', { n: l.length, m: all.length }) : t('scope.all');
    return l?.length ? t('scope.count', { n: l.length, m: all.length }) : t('scope.inherit');
  };
  const toggle = (value: string) => {
    const next = new Set(checked);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setDraft(withList(draft, target, all.filter((v) => next.has(v))));
  };
  // the project: back to every value; a building or location: every value, or none (the values above it apply)
  const setAll = (on: boolean) => setDraft(withList(draft, target, on && target.kind !== 'project' ? [...all] : []));
  const run = async () => {
    if (!q.data || !fieldId) return;
    const before = q.data.scope;
    const scope: AttributeScope = { ...before, [fieldId]: draft };
    try {
      const r = await save.mutateAsync({ projectId, scope });
      const text = r.outOfScopeTasks ? t('scope.saved', { count: r.outOfScopeTasks }) : t('scope.savedNone');
      toast.undoable(text, t('common.undo'), () => save.mutateAsync({ projectId, scope: before }).then(() => toast.ok(t('common.undone'))).catch(toast.error));
      onClose();
    } catch (e) {
      toast.error(e);
    }
  };
  const TargetButton = ({ x, label, depth = 0 }: { x: Target; label: string; depth?: number }) => (
    <li>
      <button
        type="button"
        onClick={() => setTarget(x)}
        aria-pressed={targetKey(x) === targetKey(target)}
        className={clsx('flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm', targetKey(x) === targetKey(target) ? 'bg-accent-soft text-accent' : 'hover:bg-sheet-2')}
        style={{ paddingLeft: `${0.5 + depth * 0.9}rem` }}
      >
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <span className={clsx('shrink-0 text-xs', listOf(draft, x)?.length ? 'font-medium' : 'text-graphite')}>{status(x)}</span>
      </button>
    </li>
  );
  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      id="field-scope"
      title={field ? t('scope.title', { field: field.title }) : t('scope.button')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          {q.data?.canEdit && (
            <Button variant="primary" loading={save.isPending} onClick={run}>
              {t('common.save')}
            </Button>
          )}
        </>
      }
    >
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {q.data && field && (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-graphite">{t('scope.intro')}</p>
          <div className="grid gap-4 md:grid-cols-[18rem_1fr]">
            <nav aria-label={t('scope.targets')} className="max-h-[52vh] overflow-y-auto rounded-md border border-line p-1">
              <ul className="flex flex-col gap-0.5">
                <TargetButton x={{ kind: 'project' }} label={t('scope.project')} />
              </ul>
              {buildings.length > 0 && (
                <>
                  <p className="mt-2 px-2 text-xs font-semibold text-graphite">{t('scope.buildings', { field: q.data.building!.title })}</p>
                  <ul className="flex flex-col gap-0.5">
                    {buildings.map((b) => (
                      <TargetButton key={b} x={{ kind: 'building', key: b }} label={b} />
                    ))}
                  </ul>
                </>
              )}
              {locations.length > 0 && (
                <>
                  <p className="mt-2 px-2 text-xs font-semibold text-graphite">{t('scope.locations')}</p>
                  <ul className="flex flex-col gap-0.5">
                    {locations.map((l) => (
                      <TargetButton key={l.id} x={{ kind: 'location', key: l.id }} label={l.name} depth={Math.max(0, l.depth - 1)} />
                    ))}
                  </ul>
                </>
              )}
            </nav>
            <div className="flex min-w-0 flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold">
                  {target.kind === 'project' ? t('scope.project') : target.kind === 'building' ? target.key : (locations.find((l) => l.id === target.key)?.path ?? '')}
                </span>
                {q.data.canEdit && (
                  <span className="ml-auto flex gap-1">
                    {target.kind === 'project' ? (
                      <Button size="sm" variant="ghost" onClick={() => setAll(false)}>
                        {t('scope.reset')}
                      </Button>
                    ) : (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => setAll(true)}>
                          {t('scope.selectAll')}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setAll(false)}>
                          {t('scope.selectNone')}
                        </Button>
                      </>
                    )}
                  </span>
                )}
              </div>
              <p className="text-xs text-graphite">{target.kind === 'project' ? t('scope.hintProject') : t('scope.hintPlace')}</p>
              <ul className="grid max-h-[44vh] grid-cols-1 gap-x-4 gap-y-1 overflow-y-auto rounded-md border border-line p-2 sm:grid-cols-2">
                {field.values.map((v) => (
                  <li key={v.value} className="flex items-center gap-2 text-sm">
                    <input
                      id={`scope-${v.value}`}
                      type="checkbox"
                      className="size-4 accent-[var(--accent)]"
                      checked={checked.has(v.value)}
                      // the project needs at least one value (no value at all would mean: every value)
                      disabled={!q.data!.canEdit || (target.kind === 'project' && checked.size === 1 && checked.has(v.value))}
                      onChange={() => toggle(v.value)}
                    />
                    <label htmlFor={`scope-${v.value}`} className="min-w-0 flex-1 truncate">
                      {v.value}
                      {!v.offered && <span className="ml-1 text-xs text-warn">({t('scope.notOffered')})</span>}
                    </label>
                    <span className="num shrink-0 text-xs text-graphite">{t('scope.tasks', { count: v.tasks })}</span>
                  </li>
                ))}
              </ul>
              {field.role === 'floor' && <p className="text-xs text-graphite">{t('scope.floorHint')}</p>}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

/** Summary of the scope of a field for a table cell: "alle Werte", "4 von 15", "+ 2 Gebäude/Standorte". */
export function useScopeSummary() {
  const { t } = useTranslation();
  return (s: FieldScope | undefined, total: number) => {
    const places = Object.keys(s?.byBuilding ?? {}).length + Object.keys(s?.byLocation ?? {}).length;
    const main = s?.values?.length ? t('scope.count', { n: s.values.length, m: total }) : t('scope.summaryAll');
    return places ? `${main} ${t('scope.summaryPlaces', { count: places })}` : main;
  };
}

export function FieldScopeButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <Button size="sm" variant="ghost" icon={<SlidersHorizontal className="size-3.5" />} onClick={onClick}>
      {label}
    </Button>
  );
}

/** Notice of the analysis: tasks with values outside the values of the project. */
export function OutOfScopeNotice({ tasks, fields, onEdit }: { tasks: { outOfScope?: OutOfScopeValue[] }[]; fields: { id: string; title: string }[]; onEdit?: () => void }) {
  const { t } = useTranslation();
  const rows = useMemo(() => outOfScopeSummary(tasks), [tasks]);
  if (!rows.length) return null;
  const title = new Map(fields.map((f) => [f.id, f.title]));
  const count = tasks.filter((x) => x.outOfScope?.length).length;
  const list = rows
    .slice(0, 6)
    .map((r) => `${title.get(r.field) ?? r.field} „${r.value}“ (${r.tasks})`)
    .join(', ');
  return (
    <Notice tone="warn">
      <AlertTriangle className="mr-1 inline size-4" aria-hidden />
      {t('scope.notice', { count, list: rows.length > 6 ? `${list}, …` : list })}
      {onEdit && (
        <Button size="sm" variant="ghost" className="ml-2" onClick={onEdit}>
          {t('scope.edit')}
        </Button>
      )}
    </Notice>
  );
}
